import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Checkbox, FormControlLabel, Button } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { accountActionLoading, accountInfo, sendAccountAction } from '../../store/slice/user'
import { TOAST_DEFAULT_CONFIG } from '../../constants/toast'
import { getAccountID, getToken } from '../../utils/auth'
import useStyles from './styles'

const TERMS_VERSION = 'gtc_neural_search_labs_gmbh_version_01.01.2026.pdf'
const SESSION_ACCOUNT_KEY = 'topIssuesSessionAccount'
const SESSION_TOKEN_KEY = 'topIssuesSessionToken'

const TopIssuesBanner = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const info = useSelector(accountInfo)
  const isSaving = useSelector(accountActionLoading)

  const accountId = useMemo(() => getAccountID(), [])
  const storageKey = useMemo(() => (accountId ? `topIssuesDismissed:${accountId}` : null), [accountId])
  const [dismissedIssues, setDismissedIssues] = useState(null)
  const [legalConfirmations, setLegalConfirmations] = useState({})

  useEffect(() => {
    if (!storageKey || !accountId) {
      setDismissedIssues({})
      return
    }

    try {
      const previousAccount = sessionStorage.getItem(SESSION_ACCOUNT_KEY)
      const previousToken = sessionStorage.getItem(SESSION_TOKEN_KEY)
      const currentToken = getToken()

      const isNewLogin = previousAccount !== accountId || previousToken !== currentToken
      if (previousAccount !== accountId) {
        sessionStorage.removeItem(storageKey)
      }
      if (isNewLogin) {
        sessionStorage.removeItem(storageKey)
        sessionStorage.setItem(SESSION_ACCOUNT_KEY, accountId)
        if (currentToken) sessionStorage.setItem(SESSION_TOKEN_KEY, currentToken)
      }

      const saved = sessionStorage.getItem(storageKey)
      if (saved) {
        setDismissedIssues(JSON.parse(saved))
      } else {
        setDismissedIssues({})
      }
    } catch (err) {
      setDismissedIssues({})
    }
  }, [storageKey, accountId])

  const issues = useMemo(() => info?.issues || [], [info])
  const visibleIssues = useMemo(
    () => issues.filter((issue) => !dismissedIssues?.[`${issue.type}-${issue.message}`]),
    [issues, dismissedIssues]
  )

  if (!dismissedIssues || !visibleIssues.length) return null

  const getIssueKey = (issue) => `${issue.type}-${issue.message}`

  const updateDismissed = (builder) => {
    setDismissedIssues((prev) => {
      const current = prev || {}
      const next = builder(current)
      if (storageKey) {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(next))
        } catch (err) {
          // if storage fails, still update local state so banners show/hide correctly
        }
      }
      return next
    })
  }

  const handleAccept = async (issue) => {
    if (!accountId) return

    const actionId = issue.type === 'legal' ? 'acceptTermsAndConditions' : issue.type
    const actionParameter = actionId === 'acceptTermsAndConditions' ? TERMS_VERSION : `${Date.now()}`
    const issueKey = getIssueKey(issue)

    try {
      const resp = await dispatch(
        sendAccountAction({
          accountId,
          customerId: accountId,
          customerName: info?.name,
          actionId,
          actionParameter,
        })
      ).unwrap()
      const message = resp?.Response || resp?.message || 'Thank you. Your response has been saved.'
      toast.success(message, TOAST_DEFAULT_CONFIG)
      updateDismissed((prev) => ({ ...prev, [issueKey]: true }))
    } catch (error) {
      const errMessage = error?.Response || error?.message || 'Unable to save your confirmation. Please try again.'
      toast.error(errMessage, TOAST_DEFAULT_CONFIG)
    }
  }

  const handleRemindLater = (issue) => {
    const issueKey = getIssueKey(issue)
    updateDismissed((prev) => ({ ...prev, [issueKey]: true }))
  }

  return (
    <Box className={classes.banner} role="alert" data-testid="top-issues-banner">
      {visibleIssues.map((issue) => {
        const issueKey = getIssueKey(issue)
        const isLegal = issue.type === 'legal'
        const isApiAccess = issue.type === 'APIaccess'
        const isLegalConfirmed = !!legalConfirmations[issueKey]

        return (
          <Box className={classes.issueRow} key={issueKey}>
            <Box className={classes.textContent}>
              <Typography className={classes.title} dangerouslySetInnerHTML={{ __html: issue.message }} />
              {isLegal && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={isLegalConfirmed}
                      onChange={(event) =>
                        setLegalConfirmations((prev) => ({
                          ...prev,
                          [issueKey]: event.target.checked,
                        }))
                      }
                      className={classes.checkbox}
                    />
                  }
                  className={classes.checkboxLabel}
                  label="Please check here to confirm that you have read and agree to the most recent of Terms and Conditions"
                />
              )}
            </Box>
            <Box className={classes.actions}>
              {isLegal && (
                <>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => handleAccept(issue)}
                    className={classes.cta}
                    loading={isSaving}
                    disabled={!isLegalConfirmed}
                  >
                    Accept Now
                  </LoadingButton>
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    onClick={() => handleRemindLater(issue)}
                    className={classes.remindCta}
                  >
                    Remind me later
                  </Button>
                </>
              )}
              {!isLegal && !isApiAccess && (
                <LoadingButton
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => handleAccept(issue)}
                  className={classes.cta}
                  loading={isSaving}
                >
                  Accept Now
                </LoadingButton>
              )}
              {isApiAccess && (
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => handleRemindLater(issue)}
                  className={classes.remindCta}
                >
                  Remind me later
                </Button>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default TopIssuesBanner
